import { Component, type ErrorInfo, type ReactNode } from "react";
import { ErrorPage } from "@/components/ui/error-page";
import { attemptDynamicImportRecovery, isDynamicImportChunkError } from "@/lib/dynamic-import";
import { Sentry } from "@/lib/sentry";

interface Props {
	children: ReactNode;
	fallback?: (error: unknown, reset: () => void) => ReactNode;
}

interface State {
	error: unknown;
	hasError: boolean;
	// Rozpoznany stale-chunk error po Vercel deploy: render pokazuje pusty
	// placeholder zamiast ErrorPage, a componentDidCatch probuje jednorazowy
	// reload. Gdy loop-guard odmowi reloadu, wracamy na zwykla sciezke bledu.
	recovering: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = { hasError: false, error: undefined, recovering: false };
	}

	static getDerivedStateFromError(error: unknown): State {
		return { hasError: true, error, recovering: isDynamicImportChunkError(error) };
	}

	override componentDidCatch(error: Error, info: ErrorInfo) {
		// Stale lazy chunk po Vercel deploy: React.lazy odrzucenie trafia tutaj,
		// NIE w globalny `unhandledrejection` w main.tsx (React lapie rejection),
		// wiec auto-reload musi odpalic sam boundary. Jednorazowy reload per URL
		// (loop-guard w attemptDynamicImportRecovery). (Sentry FARMASTER-FRONTEND-2H)
		if (isDynamicImportChunkError(error)) {
			if (attemptDynamicImportRecovery(error)) {
				return; // reload w toku — placeholder zostaje, bez Sentry
			}
			// loop-guard juz przeladowal ten URL — pokaz blad jak zwykle.
			this.setState({ recovering: false });
		}

		console.error("[ErrorBoundary]", error, info.componentStack);
		// Render-phase errors (np. React #185) NIE docieraja do globalnych
		// handlerow Sentry — React przechwytuje je w boundary. Tylko forward stad
		// laduje je na Sentry (ze stackiem komponentow), rownolegle do GitHub
		// issue z ErrorPage. beforeSend/IGNORED_ERROR_PATTERNS dalej filtruja szum.
		Sentry.captureException(error, {
			contexts: { react: { componentStack: info.componentStack ?? undefined } },
		});
	}

	reset = () => {
		this.setState({ hasError: false, error: undefined, recovering: false });
	};

	override render() {
		if (this.state.recovering) {
			// Strona zaraz sie przeladuje (stale chunk po deploy) — pusty placeholder
			// zamiast migniecia ErrorPage.
			return null;
		}

		if (!this.state.hasError) {
			return this.props.children;
		}

		if (this.props.fallback) {
			return this.props.fallback(this.state.error, this.reset);
		}

		return <ErrorPage error={this.state.error} reset={this.reset} />;
	}
}
